import { memo, useState } from 'react';
import { PETS_BY_ID } from '@/config/pets.config';
import { getEventByBiomeId, getEventById } from '@/config/events.config';
import { VARIANT_MULT } from '@/domain/types/pet';
import { petAssetUrl } from '@/assets/paths';
import { useGameStore } from '@/ui/store/gameStore';
import { Icon } from './Icon';
import { RarityMark, VariantMark } from './PetCollectionFilters';
import { formatNumberCompact } from '@/utils/formatNumber';
import type { PetBusyReason, PetInstance, PetDef } from '@/domain/types/pet';
import type { Rarity, Variant, ID } from '@/domain/types/common';

/**
 * PetCard — reusable karta pojedynczego peta.
 *
 * Używana w:
 *  - PetInventoryModal (sesja 21) — grid z całym inventory
 *  - MergePanel (sesja 22) — wybór 10× normal lub 12× gold do mergea
 *  - EquipPanel (sesja 22) — equipped petów (max 6 slotów)
 *  - Future: TradingPanel (sesja 5)
 *
 * Props design:
 *  - `pet`: PetInstance — co rysować
 *  - `onClick`: optional — handler klik na całą kartę (selection)
 *  - `selected`: optional — visual highlight (border)
 *  - `showStateBadges`: pokazuje lock/favorite indicators w corner (default true)
 *  - `disabled`: blokuje interakcje (np. dla zablokowanych przy merge)
 *  - `compact`: mniejsza karta (przy bulk selection)
 */

interface PetCardProps {
  pet: PetInstance;
  onClick?: (pet: PetInstance) => void;
  selected?: boolean;
  showStateBadges?: boolean;
  disabled?: boolean;
  compact?: boolean;
  /**
   * Numer slotu equipped (1-based) jeśli pet jest aktualnie wyposażony.
   * `null` lub omit = nie equipped (bez badge'a). Dodane w sesji 22d (UX
   * feedback gracza — bez tego musiał porównywać sprite'y z Phaser).
   */
  equippedSlot?: number | null;
  /**
   * Merge UX rework (decyzja A2): gdy `true`, w prawym dolnym rogu pojawia się
   * klikalny badge `⚗️` (otwiera `MergePanel` z preselectem typu peta).
   * Konsument (np. `PetInventoryModal`) wylicza per (defId, variant), czy grupa
   * ma ≥ `MERGE_RULES.normalToGold` (lub `goldToRainbow`) eligible petów.
   * Defaultowo `false` — tylko inventory pokazuje, MergePanel/Trade pomijają.
   */
  mergeReady?: boolean;
  /** Pet jest chwilowo zajęty poza inventory (w zagrodzie albo pracy bankowej). */
  busyReason?: PetBusyReason | null;
  /**
   * Karta o PRZEWIDYWALNEJ wysokości — nazwa przycięta do jednej linii, odznaki
   * bez zawijania (Codex V-4 HIGH 2).
   *
   * PO CO: `VirtualGrid` liczy pozycje wierszy mnożeniem, więc karta MUSI mieć
   * stałą wysokość. Domyślnie nazwa zawija się bez limitu („Golem z Kryształową
   * Koroną" na 320 px robi z karty 161 px zamiast 129 px), a odznaki mają
   * `flex-wrap` — pomiary Codeksa pokazały niestabilny `scrollHeight`
   * (3300 → 3485 → 3407) przy przewijaniu tej samej listy.
   *
   * DWIE linie, nie jedna (Codex V-4 FIXES LOW 1): na telefonie nie ma hovera,
   * więc `title` z pełną nazwą jest nieosiągalny. Przy jednej linii wiele petów
   * górskich wyglądało identycznie („Golem z Kryszt…"), co utrudniało dziecku
   * wybór. Dwie linie mieszczą praktycznie każdą nazwę w grze, a wysokość
   * pozostaje STAŁA — `min-h` gwarantuje ten sam wymiar także dla nazw
   * jednolinijkowych.
   *
   * OPT-IN, nie domyślne: plecak i handel mają pokazywać pełne nazwy, więc
   * zmiana dotyczy wyłącznie widoków wirtualizowanych.
   */
  fixedHeight?: boolean;
  /** Wymusza graficzne fallbacki; przydatne w beztekstowych przepływach dla dzieci. */
  assetOnly?: boolean;
  /** Pokazuje zwarte C/U/R/E/L/M + N/G/R/H/T używane w filtrach modalu petów. */
  visualTaxonomyMarks?: boolean;
}

function PetCardImpl({
  pet,
  onClick,
  selected = false,
  showStateBadges = true,
  disabled = false,
  compact = false,
  equippedSlot = null,
  mergeReady = false,
  busyReason = null,
  fixedHeight = false,
  assetOnly = false,
  visualTaxonomyMarks = false,
}: PetCardProps): JSX.Element | null {
  // 5.D.MERGE_UX FIXES (Codex MED 2): hooki MUSZĄ być wywołane PRZED jakimkolwiek
  // warunkowym return. Wcześniej `useGameStore` był po `if (!def) return null`,
  // co łamało zasadę React Hooks („hooks called unconditionally").
  const setMergePreselectKey = useGameStore((s) => s.setMergePreselectKey);
  const setOpenModal = useGameStore((s) => s.setOpenModal);

  const def = PETS_BY_ID[pet.defId];
  if (!def) {
    console.warn(`[PetCard] Unknown pet defId: ${pet.defId}`);
    return null;
  }

  // Handler dla merge badge — natywny button, klik zarządzany przez React event.
  // Po MED 1 fix badge jest SIBLING do card body (nie zagnieżdżony), więc
  // stopPropagation nie jest potrzebne — siblingi nie bąblują do siebie.
  const handleMergeBadgeClick = (): void => {
    setMergePreselectKey(`${pet.defId}::${pet.variant}`);
    setOpenModal('merge');
  };

  const power = Math.round(def.basePower * VARIANT_MULT[pet.variant]);
  const rarityBorder = rarityBorderClass(def.rarity);
  const variantGlow = variantGlowClass(pet.variant);
  const isEquipped = equippedSlot !== null && equippedSlot !== undefined;
  const eventDef = def.origin.eventId
    ? getEventById(def.origin.eventId)
    : def.origin.biomeId
      ? getEventByBiomeId(def.origin.biomeId)
      : undefined;

  const clickable = !!onClick && !disabled;

  // Priorytet ramki: selected (yellow ring) > equipped (green) > rarity color.
  // Equipped jest najsilniejszym sygnałem identyfikacyjnym dla gracza po
  // sesji 22d feedback ("nie widzę które są wyposażone"), więc zachodzi na rarity.
  const borderClass = selected
    ? 'border-yellow-400 ring-2 ring-yellow-300'
    : isEquipped
      ? 'border-meadow-500 ring-2 ring-meadow-300'
      : rarityBorder;

  return (
    // 5.D.MERGE_UX FIXES (Codex MED 1): outer to nie-interactive `<div>` —
    // card body i merge badge to RODZEŃSTWO (natywne `<button>`-y), żeby
    // uniknąć nested interactive elements. Brak custom keyboard cast'owania;
    // każdy button ma natywny Enter/Space. Tylko `relative` na wrapperze do
    // pozycjonowania badge'a `absolute bottom-1 right-1`.
    <div className="relative">
      <button
        type="button"
        onClick={clickable ? () => onClick(pet) : undefined}
        disabled={!clickable}
        className={[
          'relative flex flex-col items-center rounded-2xl transition w-full',
          compact ? 'p-2' : 'p-3',
          'border-2',
          borderClass,
          variantGlow,
          clickable ? 'cursor-pointer hover:scale-105 active:scale-95 bg-meadow-50' : 'bg-meadow-50/60',
          disabled ? 'opacity-50' : '',
        ].join(' ')}
        aria-label={`${def.name.pl}, ${def.rarity}, moc ${formatNumberCompact(power)}${isEquipped ? `, wyposażony w slot ${equippedSlot}` : ''}${busyReason === 'stall' ? ', wystawiony w zagrodzie' : busyReason === 'job' ? ', pracuje w banku' : ''}`}
      >
        {/* State badges visual-only (NIE interactive) — sesja 28c: <Icon> z assets/ui/. */}
        {showStateBadges && (
          <>
            {pet.locked && (
              <Icon
                src="/ui/ui_btn_lock.webp"
                fallback={assetOnly ? '' : '🔒'}
                alt="Zablokowany"
                className={`absolute top-1 left-1 ${compact ? 'w-4 h-4' : 'w-5 h-5'}`}
              />
            )}
            {pet.favorite && (
              <Icon
                src="/ui/ui_btn_favorite.webp"
                fallback={assetOnly ? '' : '⭐'}
                alt="Ulubiony"
                className={`absolute top-1 right-1 ${compact ? 'w-4 h-4' : 'w-5 h-5'}`}
              />
            )}
            {busyReason && (
              <span
                className={`absolute top-1 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-slate-700 text-white font-extrabold shadow ${
                  compact ? 'px-1 py-0.5 text-[8px]' : 'px-1.5 py-0.5 text-[9px]'
                }`}
                title={busyReason === 'stall' ? 'Pet jest wystawiony w zagrodzie' : 'Pet pracuje w banku'}
              >
                {busyReason === 'stall' ? 'ZAGRODA' : 'PRACA'}
              </span>
            )}
            {/* Equipped slot badge — bottom-left, zielony chip z numerem slotu.
                Sesja 22d: gracz musi widzieć w inventory które pety są wyposażone
                i w którym slocie, bez patrzenia na Phaser. */}
            {isEquipped && (
              <span
                className={`absolute bottom-1 left-1 bg-meadow-500 text-white font-bold rounded-full flex items-center justify-center shadow ${
                  compact ? 'w-5 h-5 text-[10px]' : 'w-6 h-6 text-xs'
                }`}
                title={`Wyposażony — slot ${equippedSlot}`}
              >
                {equippedSlot}
              </span>
            )}
          </>
      )}

      {/* Pet sprite */}
      <PetImage
        textureKey={def.art.textureKey}
        biomeId={def.origin?.biomeId ?? 'biome-meadow'}
        alt={def.name.pl}
        compact={compact}
      />

      {/* Name */}
      <span
        className={`font-bold text-meadow-700 text-center mb-0.5 ${compact ? 'text-xs' : 'text-sm'} ${
          fixedHeight ? 'line-clamp-2 min-h-[2.5em] leading-tight w-full px-1' : ''
        }`}
        title={fixedHeight ? def.name.pl : undefined}
      >
        {def.name.pl}
      </span>

      {/* Rarity + variant badges */}
      <div
        className={`flex gap-1 mb-1 justify-center items-center ${
          fixedHeight ? 'flex-nowrap overflow-hidden' : 'flex-wrap'
        }`}
      >
        {visualTaxonomyMarks ? (
          <>
            <RarityMark value={def.rarity} />
            <VariantMark value={pet.variant} />
          </>
        ) : (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${rarityBadgeClass(def.rarity)}`}>
            {rarityShortLabel(def.rarity)}
          </span>
        )}
        {eventDef && (
          <span
            className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cyan-500 text-white"
            title={eventDef.name.pl}
          >
            EVENT
          </span>
        )}
        {!visualTaxonomyMarks && pet.variant !== 'normal' && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${variantBadgeClass(pet.variant)}`}>
            {pet.variant === 'rainbow' ? (
              <Icon
                src="/ui/ui_variant_rainbow.webp"
                fallback={assetOnly ? '' : '🌈'}
                alt="rainbow"
                className="w-3 h-3"
              />
            ) : (
              variantShortLabel(pet.variant)
            )}
          </span>
        )}
      </div>

      {/* Power */}
      <div className="flex items-center gap-1">
        <Icon src="/ui/ui_power.webp" fallback={assetOnly ? '' : '⚡'} alt="moc" className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        <span className={`font-bold text-blue-700 ${compact ? 'text-xs' : 'text-sm'}`}>
          {formatNumberCompact(power)}
        </span>
      </div>
      </button>

      {/* Merge UX rework (decyzja A2) — natywny <button> SIBLING do card body
          (NIE nested). Pojawia się w prawym dolnym rogu, pulsuje fioletowo,
          żeby przyciągnąć wzrok dziecka. `z-10` żeby był nad krawędzią karty,
          ale jako sibling DOM-owo wcale nie konkurujemy o event bubbling —
          klik trafia tylko tu, nie do card body buttona pod spodem. */}
      {showStateBadges && mergeReady && !disabled && (
        <button
          type="button"
          onClick={handleMergeBadgeClick}
          className={`absolute bottom-1 right-1 z-10 bg-purple-500 hover:bg-purple-600 active:scale-95 rounded-full flex items-center justify-center shadow-lg ring-2 ring-purple-200 cursor-pointer animate-pulse focus-visible:ring-4 focus-visible:ring-purple-400 ${
            compact ? 'w-6 h-6 p-1' : 'w-7 h-7 p-1.5'
          }`}
          title="Możesz łączyć! Kliknij, aby otworzyć panel łączenia."
          aria-label={`Możesz łączyć ${def.name.pl} — otwórz panel łączenia`}
        >
          <Icon
            src="/ui/ui_btn_merge.webp"
            fallback={assetOnly ? '' : '⚗'}
            alt=""
            className="w-full h-full"
          />
        </button>
      )}
    </div>
  );
}

function PetImage({
  textureKey,
  biomeId,
  alt,
  compact,
}: {
  textureKey: string;
  biomeId: ID;
  alt: string;
  compact: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const size = compact ? 'w-12 h-12' : 'w-16 h-16';

  if (failed) {
    return (
      <Icon
        src="/ui/ui_btn_pets.webp"
        fallback=""
        alt={alt}
        className={`${size} mb-1 object-contain opacity-60`}
      />
    );
  }

  return (
    <img
      src={petAssetUrl(textureKey, biomeId)}
      alt={alt}
      className={`${size} object-contain mb-1`}
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
}

// ── Style helpers — kolory rarity/variant w 1 miejscu ─────────────────────

function rarityBorderClass(rarity: Rarity): string {
  switch (rarity) {
    case 'common':    return 'border-gray-300';
    case 'uncommon':  return 'border-green-400';
    case 'rare':      return 'border-blue-400';
    case 'epic':      return 'border-purple-400';
    case 'legendary': return 'border-yellow-400';
    case 'mythic':    return 'border-red-400';
  }
}

function rarityBadgeClass(rarity: Rarity): string {
  switch (rarity) {
    case 'common':    return 'bg-gray-400 text-white';
    case 'uncommon':  return 'bg-green-500 text-white';
    case 'rare':      return 'bg-blue-500 text-white';
    case 'epic':      return 'bg-purple-500 text-white';
    case 'legendary': return 'bg-yellow-500 text-white';
    case 'mythic':    return 'bg-red-500 text-white';
  }
}

function rarityShortLabel(rarity: Rarity): string {
  switch (rarity) {
    case 'common':    return 'C';
    case 'uncommon':  return 'U';
    case 'rare':      return 'R';
    case 'epic':      return 'E';
    case 'legendary': return 'L';
    case 'mythic':    return 'M';
  }
}

function variantBadgeClass(variant: Variant): string {
  switch (variant) {
    case 'gold':    return 'bg-yellow-400 text-black';
    case 'rainbow': return 'bg-gradient-to-r from-pink-400 via-yellow-400 to-blue-400 text-white';
    case 'huge':    return 'bg-red-500 text-white';
    case 'titanic': return 'bg-gradient-to-r from-violet-500 via-fuchsia-500 to-indigo-500 text-white';
    default:        return 'bg-gray-300 text-black';
  }
}

function variantShortLabel(variant: Variant): string {
  switch (variant) {
    case 'gold':    return 'GOLD';
    case 'rainbow': return 'RAINBOW';
    case 'huge':    return 'HUGE';
    case 'titanic': return 'TITANIC';
    default:        return '';
  }
}

function variantGlowClass(variant: Variant): string {
  // Subtle visual hint for non-normal variants. Pełny shader/glow effect w sesji 23.
  switch (variant) {
    case 'gold':    return 'shadow-[0_0_8px_rgba(255,210,74,0.6)]';
    case 'rainbow': return 'shadow-[0_0_8px_rgba(255,105,180,0.6)]';
    case 'huge':    return 'shadow-[0_0_12px_rgba(239,68,68,0.7)]';
    case 'titanic': return 'shadow-[0_0_16px_rgba(168,85,247,0.8)]';
    default:        return '';
  }
}

/**
 * Memoizowany (runda PERF): re-render TYLKO gdy zmienią się propsy. Skuteczne, bo
 * PetCard subskrybuje wyłącznie stabilne settery store (setMergePreselectKey /
 * setOpenModal). Przy ~1,5k petów + delcie zmieniającej kilka — re-renderują się
 * tylko zmienione karty zamiast całego gridu. Wymaga stabilnego `onClick`
 * (useCallback u konsumenta) i zachowania identyczności obiektów `pet` (reduktor
 * delty mutuje immutable — nietknięte pety zachowują referencję).
 */
export const PetCard = memo(PetCardImpl);

// Re-export PetDef type dla konsumentów PetCard
export type { PetDef };
